'use client';

import {useTranslations,useLocale} from "next-intl";
import React from 'react';

import IconDentalChair from '@/app-components/resouces/bg-dpc/IconDentalChair';
import IconDentalLab from '@/app-components/resouces/bg-dpc/IconDentalLab';
import ReviewerAvatarClient from "@/app-components/resouces/bg-dpc/ReviewerAvatarClient";
import CopyToClipboard from "@/app-components/resouces/CopyToClipboard";
import ResourceMarkdown from '@/app-components/resouces/ResourceMarkdown';
import { BgDpcCodeType } from '@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcCodeType';
import {BgDpcRecipeType} from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcRecipeType";
import {Link} from "@/i18n/routing";
import DateUtils from "@/utils/DateUtils";
import URLUtils from "@/utils/URLUtils";

export type ItemDentalTreatmentInteractiveProps = {
	searchQuery: string;
	codes: BgDpcCodeType[];
	treatment: BgDpcRecipeType;
	treatments: BgDpcRecipeType[];
};

export default function ItemDentalTreatmentInteractive({ treatment, treatments, codes, searchQuery }: ItemDentalTreatmentInteractiveProps) {
	const locale:string = useLocale();

	const t = useTranslations('page.r-bg-dental-procedure-codes');
	const tCat = useTranslations( 'page.r-bg-dental-procedure-codes-catalog');
	const tSingle = useTranslations( 'page.r-bg-dental-procedure-codes-single-treatment');

    let relatedTreatments: BgDpcRecipeType[] | null = [];

    if (treatment.related) {
	    for (const recipe of treatments) {
	        if (treatment.related.includes(recipe.slug)) {
	            relatedTreatments.push(recipe);
	        }
	    }
    }
    if (relatedTreatments.length === 0) {
        relatedTreatments = null;
    }

	return (
		<div className="card border p-4 h-100">
			<div className="card-body p-0">
				<div className="d-flex justify-content-between">
					<h3 className="card-title mb-0 h5">
						<Link href={treatment.siteLink} className="heading-color">
							{treatment.name}
						</Link>
					</h3>

					<CopyToClipboard textToCopy={URLUtils.path([locale, treatment.siteLink])}>
						<a className="btn btn-sm btn-light btn-icon" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.copy_link')}>
							<i className="bi bi-link" title={tCat('common-tooltip.copy_link')}></i>
						</a>
					</CopyToClipboard>
				</div>

				<div className="d-flex justify-content-between mb-3">
					<div>
						<div className="card-title mb-0 text-body heading-color">
							{treatment.category.name}
						</div>
						<div className="mb-3 small">
							{t('common_used.last_change') + ': ' + DateUtils.formatDate(treatment.updatedAt, locale)}
						</div>
					</div>
				</div>

				{treatment.description &&
					<ResourceMarkdown codes={codes}>
						{treatment.description}
                    </ResourceMarkdown>
				}


				{treatment.codesToUse &&
                    <>
                        <hr className="my-2"/>
                        <ResourceMarkdown codes={codes}>
							{treatment.codesToUse.description}
                        </ResourceMarkdown>

						{treatment.codesToUse.codes &&
                            <div className="row px-4">
                                <ul className="list-group list-group-borderless mb-4">
									{treatment.codesToUse.codes.map((codeItem) => (
										<li key={codeItem.code.code} className="list-group-item mb-0">
											<Link href={codeItem.code.siteLink} className="btn border btn-sm btn-light font-monospace fw-semibold me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={codeItem.code.name}>
												{codeItem.code.code}
											</Link>

											<CopyToClipboard textToCopy={codeItem.code.code}>
												<a className="btn btn-sm btn-light btn-icon me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.copy_code')}>
													<i className="bi bi-copy" title={tCat('common-tooltip.copy_code')}></i>
												</a>
											</CopyToClipboard>

											{codeItem.code.popular &&
												<i className="fas fa-star text-warning me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.popular')} title={tCat('common-tooltip.popular')}></i>
											}
											{codeItem.code.chairSide &&
												<IconDentalChair />
											}
											{codeItem.code.dentalLab &&
												<IconDentalLab />
											}
											<ResourceMarkdown codes={codes} pToSpan={true}>
												{codeItem.explanation}
											</ResourceMarkdown>
										</li>
									))}
                                </ul>
                            </div>
						}
                    </>
				}


				{treatment.additionalCodes &&
                    <>
                        <hr className="my-2"/>
                        <ResourceMarkdown codes={codes}>
							{treatment.additionalCodes.description}
                        </ResourceMarkdown>

						{treatment.additionalCodes.codes &&
                            <div className="row px-4">
                                <ul className="list-group list-group-borderless mb-4">
									{treatment.additionalCodes.codes.map((codeItem) => (
										<li key={codeItem.code.code} className="list-group-item mb-0">
											<Link href={codeItem.code.siteLink} className="btn border btn-sm btn-light font-monospace fw-semibold me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={codeItem.code.name}>
												{codeItem.code.code}
											</Link>

											<CopyToClipboard textToCopy={codeItem.code.code}>
												<a className="btn btn-sm btn-light btn-icon me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.copy_code')}>
													<i className="bi bi-copy" title={tCat('common-tooltip.copy_code')}></i>
												</a>
											</CopyToClipboard>

											

											{codeItem.code.popular &&
												<i className="fas fa-star text-warning me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.popular')} title={tCat('common-tooltip.popular')}></i>
											}
											{codeItem.code.chairSide &&
												<IconDentalChair />
											}
											{codeItem.code.dentalLab &&
												<IconDentalLab />
											}

											<ResourceMarkdown codes={codes} pToSpan={true}>
												{codeItem.explanation}
											</ResourceMarkdown>
										</li>
									))}
                                </ul>
                            </div>
						}
                    </>
				}


				{relatedTreatments &&
                   <RelatedTreatmentsCard
                    title={tSingle('related_treatments_section.title')}
                    treatments={relatedTreatments}
                    />
				}
				<hr className="my-2"/>


				<div className="d-sm-flex justify-content-center justify-content-md-between align-items-center text-start mt-5">
					{treatment.writer &&
                        <ReviewerAvatarClient role='write' author={treatment.writer}/>
					}

					{treatment.reviewer &&
                        <ReviewerAvatarClient role='review' author={treatment.reviewer}/>
					}
				</div>
			</div>
		</div>
	);
}


function RelatedTreatmentsCard({ title, treatments }: { title:string, treatments: BgDpcRecipeType[]}) {

	return (
        <>
            <hr className="my-2"/>
            <h6 className="mt-4">
                { title }
            </h6>
            <ul className="mb-4">
				{treatments.map((recipeItem) => (
					<li key={recipeItem.slug} className="heading-color">
						<Link href={recipeItem.siteLink} className="heading-color">
							{recipeItem.name}
						</Link>
					</li>
				))}
            </ul>
        </>
	);
}

